"use client";

import { useContext, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { HeartFilledIcon } from "@radix-ui/react-icons";
import { Edit, Eye, EyeOff, Heart, Trash, Upload, Wand2 } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/router";
import { LoraDetailsSkeleton } from "@/components/atoms/Skeletons/LoraDetails";
import UserCard from "@/components/atoms/UserCard";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { deleteLora, fetchLora, likeLora, updateLora } from "@/lib/actions";
import { AppContext } from "@/lib/contexts";
import { AppReducerActionType, LoraDTO } from "@/types";
type LoraDetailsProps = {
  loraId: string;
};
export default function LoraDetails({
  loraId
}: LoraDetailsProps) {
  const [loading, setLoading] = useState(true);
  const {
    state,
    dispatch
  } = useContext(AppContext);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const router = useRouter();
  useEffect(() => {
    const fetchLoraDetails = async () => {
      setLoading(true);
      const response = await fetchLora(loraId);
      console.log(response);
      if (response.lora) {
        setEditedLora(response.lora);
      } else {
        setEditedLora(null);
      }
      setLoading(false);
    };
    fetchLoraDetails();
  }, [loraId]);
  const [editedLora, setEditedLora] = useState(null as LoraDTO | null);
  if (loading && !editedLora) {
    return <LoraDetailsSkeleton />;
  }
  if (!editedLora) {
    return <div className="min-h-screen bg-background text-foreground flex items-center justify-center">
                LORA not found
            </div>;
  }
  const isOwner = state.user?.userId === editedLora?.userDTO.userId;

  // const [newComment, setNewComment] = useState("");

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setEditedLora({
      ...editedLora,
      [e.target.name]: e.target.value
    });
  };
  const handleSave = async (loraToSave: LoraDTO) => {
    const response = await updateLora(loraToSave);
    if (response.lora) {
      toast.success("LORA updated successfully");
    } else {
      toast.error("Failed to update LORA");
    }
    setIsDialogOpen(false);
  };

  // const handleAddComment = () => {
  //     if (newComment.trim() && state.user?.name) {
  //         const comment: Comment = {
  //             id: Date.now().toString(),
  //             user: {
  //                 name: user.name,
  //                 avatar: "/placeholder.svg?height=40&width=40",
  //             },
  //             content: newComment,
  //             createdAt: new Date().toISOString(),
  //         };
  //         setEditedLora((prevLora) => ({
  //             ...prevLora,
  //             comments: [...(prevLora.comments || []), comment],
  //         }));
  //         setNewComment("");
  //     }
  // };

  const togglePublic = () => {
    const updatedLora = {
      ...editedLora,
      isPublic: !editedLora.isPublic
    };
    setEditedLora(updatedLora);
    handleSave(updatedLora);
  };
  const confirmDelete = () => {
    setIsDeleteDialogOpen(true);
  };
  const handleConfirmDelete = async () => {
    setIsDeleteDialogOpen(false);
    const response = await deleteLora(editedLora.id);
    if (response.success) {
      toast.success("LORA deleted successfully");
      router.push("/loras");
    } else {
      toast.error("Failed to delete LORA");
    }
  };
  const handleLike = async () => {
    if (!state.isLoggedIn) {
      dispatch({
        type: AppReducerActionType.SHOW_AUTH_MODAL,
        payload: {
          ...state,
          showAuthModal: true
        }
      });
      return;
    }
    if (editedLora.isLiked) {
      setEditedLora(prevLora => {
        if (!prevLora) return prevLora;
        return {
          ...prevLora,
          likes: prevLora.likes - 1,
          isLiked: false
        };
      });
    } else {
      setEditedLora(prevLora => {
        if (!prevLora) return prevLora;
        return {
          ...prevLora,
          likes: prevLora.likes + 1,
          isLiked: true
        };
      });
    }
    const response = await likeLora(editedLora.id);
    if (response.success) {
      if (!editedLora.isLiked) {
        toast.success("Liked LORA");
      } else {
        toast.success("Unliked LORA");
      }
    } else {
      toast.error("Failed to like LORA");
    }
  };
  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const updatedLora = {
          ...editedLora,
          coverImage: reader.result as string
        };
        setEditedLora(updatedLora);
        handleSave(updatedLora);
      };
      reader.readAsDataURL(file);
    }
  };
  const triggerImageUpload = () => {
    fileInputRef.current?.click();
  };
  return <div className="container mx-auto px-4 py-6 md:py-8" data-sentry-component="LoraDetails" data-sentry-source-file="index.tsx">
            <div className="bg-slate-900 backdrop-blur-lg text-gray-100 rounded-lg overflow-hidden shadow-2xl border border-slate-700">
                <div className="relative h-64 md:h-96">
                    <Image src={editedLora.coverImage || "/placeholder.svg"} alt={editedLora.loraName} fill style={{
          objectFit: "cover"
        }} priority data-sentry-element="Image" data-sentry-source-file="index.tsx" />
                    {isOwner && <Button variant="secondary" size="sm" className="absolute bottom-4 right-4 bg-gray-800/60 hover:bg-gray-700/60" onClick={triggerImageUpload}>
                            <Upload className="w-4 h-4 mr-2" />
                            Update Cover
                        </Button>}
                    <input type="file" ref={fileInputRef} className="hidden" accept="image/*" onChange={handleImageUpload} />
                </div>
                <div className="p-4 md:p-6">
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4 space-y-4 md:space-y-0">
                        <h1 className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-blue-700 to-sky-500 text-transparent bg-clip-text">
                            {editedLora.loraName}
                        </h1>
                        <div className="flex flex-wrap items-center gap-2 md:gap-4">
                            <Button variant="ghost" size="sm" className="flex items-center text-muted-foreground" onClick={handleLike} data-sentry-element="Button" data-sentry-source-file="index.tsx">
                                {editedLora.isLiked ? <HeartFilledIcon className="text-rose-500" /> : <Heart className="h-4 w-4 fill-none" />}
                                {editedLora.likes}
                            </Button>
                            {isOwner && <Button variant="ghost" size="sm" onClick={togglePublic} className="flex items-center text-muted-foreground">
                                    {editedLora.isPublic ? <Eye className="h-4 w-4 mr-2 text-blue-500" /> : <EyeOff className="h-4 w-4 mr-2 text-red-500" />}
                                    {editedLora.isPublic ? "Public" : "Private"}
                                </Button>}
                            {isOwner && <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                                    <DialogTrigger asChild>
                                        <Button variant="ghost" size="sm">
                                            <Edit className="w-5 h-5" />
                                        </Button>
                                    </DialogTrigger>
                                    <DialogContent className="sm:max-w-[425px] bg-gray-800 text-gray-100 border border-gray-700">
                                        <DialogHeader>
                                            <DialogTitle className="text-gradient">
                                                Edit LORA
                                            </DialogTitle>
                                        </DialogHeader>
                                        <div className="grid gap-4 py-4">
                                            <div className="grid grid-cols-4 items-center gap-4">
                                                <Label htmlFor="name" className="text-right">
                                                    Name
                                                </Label>
                                                <Input id="name" name="name" value={editedLora.loraName} onChange={handleChange} className="col-span-3 bg-gray-700 text-gray-100 border-gray-600" />
                                            </div>
                                            <div className="grid grid-cols-4 items-center gap-4">
                                                <Label htmlFor="shortDescription" className="text-right">
                                                    Description
                                                </Label>
                                                <Textarea id="shortDescription" name="shortDescription" value={editedLora.shortDescription} onChange={handleChange} className="col-span-3 bg-gray-700 text-gray-100 border-gray-600" />
                                            </div>
                                        </div>
                                        <Button onClick={() => handleSave(editedLora)} className="bg-gradient-to-r from-emerald-600 to-green-500 text-white hover:from-emerald-700 hover:to-green-600">
                                            Save changes
                                        </Button>
                                    </DialogContent>
                                </Dialog>}
                            {isOwner && <Button variant="ghost" size="sm" onClick={confirmDelete} className="flex items-center text-muted-foreground">
                                    <Trash className="h-4 w-4 mr-2 text-red-500" />
                                </Button>}
                            <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen} data-sentry-element="Dialog" data-sentry-source-file="index.tsx">
                                <DialogContent className="sm:max-w-[425px] bg-gray-800 text-gray-100 border border-gray-700" data-sentry-element="DialogContent" data-sentry-source-file="index.tsx">
                                    <DialogHeader data-sentry-element="DialogHeader" data-sentry-source-file="index.tsx">
                                        <DialogTitle className="text-gradient" data-sentry-element="DialogTitle" data-sentry-source-file="index.tsx">
                                            Confirm Deletion
                                        </DialogTitle>
                                    </DialogHeader>
                                    <div className="py-4">
                                        <p>
                                            Are you sure you want to delete this
                                            LORA?
                                        </p>
                                    </div>
                                    <div className="flex justify-end space-x-2">
                                        <Button variant="secondary" onClick={() => setIsDeleteDialogOpen(false)} className="bg-gray-700 text-gray-100 border-gray-600" data-sentry-element="Button" data-sentry-source-file="index.tsx">
                                            Cancel
                                        </Button>
                                        <Button onClick={handleConfirmDelete} className="bg-red-600 text-white hover:bg-red-700" data-sentry-element="Button" data-sentry-source-file="index.tsx">
                                            Delete
                                        </Button>
                                    </div>
                                </DialogContent>
                            </Dialog>
                            <Link href={`/generate?loraId=${editedLora.id}&model=Flux.Pro`} data-sentry-element="Link" data-sentry-source-file="index.tsx">
                                <Button size="sm" className="bg-gradient-to-r from-emerald-600 to-green-500 text-white hover:from-emerald-700 hover:to-green-600 transform hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-xl" data-sentry-element="Button" data-sentry-source-file="index.tsx">
                                    <Wand2 className="mr-2 h-5 w-5" data-sentry-element="Wand2" data-sentry-source-file="index.tsx" />
                                    Use This LORA
                                </Button>
                            </Link>
                        </div>
                    </div>

                    <p className="text-sm md:text-base text-muted-foreground mb-4">
                        {editedLora.shortDescription}
                    </p>

                    <div className="mb-6">
                        <h3 className="text-lg font-semibold mb-2 text-gray-400 bg-clip-text">
                            Created By
                        </h3>
                        <UserCard user={editedLora.userDTO} data-sentry-element="UserCard" data-sentry-source-file="index.tsx" />
                    </div>

                    <div className="mb-4">
                        <h3 className="text-lg font-semibold mb-2 text-gray-400 bg-clip-text">
                            Trigger Words
                        </h3>
                        <Badge key={`trigger-${editedLora.triggerWord}`} variant="secondary" className="bg-gradient-to-r from-blue-700 to-sky-500 px-3 py-1 !rounded-full text-sm font-medium shadow-md hover:shadow-lg transition-all duration-200 hover:from-blue-800 hover:to-sky-600" data-sentry-element="Badge" data-sentry-source-file="index.tsx">
                            {editedLora.triggerWord}
                        </Badge>
                    </div>

                    <div className="text-xs md:text-sm  text-gray-400 mb-6">
                        <p>
                            Created:{" "}
                            {new Date(editedLora.createdAt || Date.now()).toLocaleDateString()}
                        </p>
                        <p>
                            Last updated:{" "}
                            {new Date(editedLora.updatedAt || Date.now()).toLocaleDateString()}
                        </p>
                    </div>

                    <Separator className="my-6 bg-gradient-to-r from-blue-700 to-sky-500 h-px" data-sentry-element="Separator" data-sentry-source-file="index.tsx" />

                    {/* <div className="mt-6">
                        <h2 className="text-xl font-semibold mb-4 text-gradient">
                            Comments
                        </h2>
                        <div className="space-y-4 mb-4">
                            {editedLora.comments?.map((comment) => (
                                <div
                                    key={comment.id}
                                    className="flex space-x-3"
                                >
                                    <Avatar>
                                        <AvatarImage
                                            src={comment.user.avatar}
                                            alt={comment.user.name}
                                        />
                                        <AvatarFallback>
                                            {comment.user.name[0]}
                                        </AvatarFallback>
                                    </Avatar>
                                    <div>
                                        <p className="font-semibold">
                                            {comment.user.name}
                                        </p>
                                        <p className="text-sm text-muted-foreground">
                                            {new Date(
                                                comment.createdAt
                                            ).toLocaleString()}
                                        </p>
                                        <p className="mt-1">
                                            {comment.content}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="flex space-x-2">
                            <Input
                                placeholder="Add a comment..."
                                value={newComment}
                                onChange={(e) => setNewComment(e.target.value)}
                                className="flex-grow bg-muted"
                            />
                            <Button
                                onClick={handleAddComment}
                                disabled={!user?.name}
                                className="bg-accent text-accent-foreground hover:bg-accent/90 transform hover:scale-105 transition-all duration-200"
                            >
                                Post
                            </Button>
                        </div>
                     </div> */}
                </div>
            </div>
        </div>;
}